import type { ProgressStep } from "../api/types";

/**
 * The zone report dates are read in: the server's `TIME_ZONE`, which the
 * Django pages format in. Without it the browser's own zone applies, and a
 * viewer elsewhere would see a different day near midnight than the Django
 * app shows for the same row.
 */
export const REPORT_TIME_ZONE = "America/Toronto";

function parse(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function locale(lang: string): string {
  return lang === "en" ? "en-CA" : "fr-CA";
}

/** Locale-aware short date; empty string for null/blank input. */
export function formatDate(value: string | null | undefined, lang: string): string {
  const date = parse(value);
  if (!date) return "";
  return date.toLocaleDateString(locale(lang), {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: REPORT_TIME_ZONE,
  });
}

/** Locale-aware short date and time; empty string for null/blank input. */
export function formatDateTime(value: string | null | undefined, lang: string): string {
  const date = parse(value);
  if (!date) return "";
  return date.toLocaleString(locale(lang), {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: REPORT_TIME_ZONE,
  });
}

/** Tone for a progress step — later steps read as more "complete". */
export function stepTone(
  step: string,
): "neutral" | "info" | "warning" | "success" {
  const index = Number(step.replace("step_", ""));
  if (index >= 6) return "success";
  if (index >= 3) return "info";
  if (index >= 1) return "warning";
  return "neutral";
}

export function isProgressStep(value: string): value is ProgressStep {
  return /^step_[0-6]$/.test(value);
}
